/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  CellClassParams,
  CellStyle,
  ColDef,
  GridOptions,
  GridReadyEvent,
  ModelUpdatedEvent,
  RowNode,
} from '@ag-grid-community/core'
import { Component, Input, OnDestroy, OnInit } from '@angular/core'
import { FormControl } from '@angular/forms'
import {
  enableHeliosCopyBehavior,
  enableHeliosGridDensityLevel,
  enableHeliosMultiRowDragSelection,
  heliosClientSideRowConfig,
  heliosDefaultColDef,
  heliosMultiRowSelectionConfig,
} from '@helios/ag-grid-config'
import { HighlightingService } from '@helios/generic-components'
import {
  HighlightRowsInExternalFileViewer,
  HotkeyDestroyFn,
  HotkeysService,
} from '@helios/hotkeys'
import { SavedView } from '@helios/state-management'
import {
  DensityLevel,
  MemoryStorageService,
  UserPreferencesService,
  WorkspaceGridSavedViewService,
} from '@helios/user-preferences'
import { AgGridApi } from '@northfork/ag-grid'
import { Subject } from 'rxjs'
import { debounceTime, takeUntil } from 'rxjs/operators'
import { ExternalFileViewerDetailsGridKey } from '../../panel-key'
import {
  contextMenuItems,
  generateUniqueFileKey,
} from '../external-file-viewer.utils'
import { ExternalFileViewerService } from '../services/external-file-viewer.service'
import { FileData, FileDataModel } from '../models/external-file-viewer'
import { CustomAggregateBarComponent } from './external-details-agg-status-bar/customAggregateBarComponent'

const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000
const ONE_DAY_MS = 24 * 60 * 60 * 1000

@Component({
  selector: 'hs-external-file-viewer-details',
  templateUrl: './external-file-viewer-details.component.html',
  styleUrls: ['./external-file-viewer-details.component.scss'],
  providers: [HighlightingService],
})
export class ExternalFileViewerDetailsComponent implements OnInit, OnDestroy {
  private readonly hotkeyDestroyStack: HotkeyDestroyFn[] = []
  constructor(
    readonly externalFileViewerService: ExternalFileViewerService,
    private readonly preferences: UserPreferencesService,
    private readonly savedViewService: WorkspaceGridSavedViewService,
    private readonly highlightService: HighlightingService,
    private readonly memoryStorageService: MemoryStorageService,
    private readonly hotkeysService: HotkeysService<HighlightRowsInExternalFileViewer>,
  ) {}
  gridOptions: GridOptions = this.getGridOptionsData()

  agGridApi?: AgGridApi

  externalFileDetails$ = new Subject<any[]>()
  searchText = new FormControl('')
  failedToLoadData = false

  @Input() savedViews: SavedView[] | undefined | null
  private readonly destroySubject$ = new Subject<void>()

  @Input() externalRawData: FileDataModel | undefined

  ngOnInit(): void {
    this.searchText.valueChanges
      .pipe(takeUntil(this.destroySubject$), debounceTime(500))
      .subscribe(() => {
        this.agGridApi?.api.onFilterChanged()
        this.agGridApi?.api.redrawRows()
      })
    this.registerHotkeys()
  }

  ngOnDestroy(): void {
    this.unregisterHotkeys()
    this.destroySubject$.next()
    this.destroySubject$.complete()
  }

  async fetchFileData(selectedRawData: FileDataModel) {
    const endReconDate = selectedRawData.endReconDate
      ? selectedRawData.endReconDate
      : selectedRawData.reconDate

    await this.getExternalFileDetails(endReconDate, selectedRawData)
  }

  async getExternalFileDetails(
    endReconDate: string,
    selectedRawData: FileDataModel,
  ) {
    const weeklyDateRanges = this.getOneWeekISODateRanges(
      new Date(selectedRawData.startReconDate),
      new Date(endReconDate),
    )
    const columns = new Set<string>()

    try {
      const files: FileData[] = (
        await Promise.all(
          weeklyDateRanges.map(([begin, end]) =>
            this.externalFileViewerService.fetchFileById(
              selectedRawData.fileId,
              begin,
              end,
              selectedRawData.accountId,
            ),
          ),
        )
      ).flat()

      files.forEach(({ columnNames }) =>
        columnNames.forEach(cn => columns.add(cn)),
      )

      const rowData: { [key: string]: any }[] = files.flatMap(data =>
        this.fileDataToRowData(data),
      )

      this.agGridApi?.api.setColumnDefs(
        this.fileDetailsColDefs(Array.from(columns)),
      )
      this.externalFileDetails$.next(rowData)
      this.setExternalFilter(selectedRawData.filterText ?? '')
      this.failedToLoadData = false
    } catch (e) {
      this.agGridApi?.api.showNoRowsOverlay()
      this.failedToLoadData = true
      return
    }
  }

  private getOneWeekISODateRanges(start: Date, end: Date): [string, string][] {
    const dateRanges: [string, string][] = []

    let currentEndDate = new Date(end)
    const beginDate = new Date(start)
    const beginDateTime = beginDate.getTime()

    while (currentEndDate.getTime() >= beginDateTime) {
      const currStartDate = new Date(
        Math.max(currentEndDate.getTime() - ONE_WEEK_MS, beginDateTime),
      )

      const startDate = currStartDate.toISOString().split('T')[0]
      const currEndDate = currentEndDate.toISOString().split('T')[0]

      if (startDate != null && currEndDate != null) {
        dateRanges.push([startDate, currEndDate])
      }

      currentEndDate = new Date(currStartDate.getTime() - ONE_DAY_MS)
    }

    return dateRanges
  }

  fileDataToRowData(rawFileData: FileData): { [key: string]: any }[] {
    return rawFileData.rawData.map(data => {
      const obj: { [key: string]: any } = {}

      for (let i = 0; i < rawFileData.columnNames.length; i++) {
        obj[rawFileData.columnNames[i]] = data[i]
      }

      return obj
    })
  }

  getGridOptionsData(): GridOptions {
    return {
      ...heliosClientSideRowConfig,
      ...heliosMultiRowSelectionConfig,
      onGridReady: (event: GridReadyEvent) => this.onGridReady(event),
      defaultColDef: {
        ...heliosDefaultColDef,
        sortable: true,
        cellStyle: (params: CellClassParams) => this.getCellStyle(params),
        cellClass: (params: CellClassParams) => this.getCellClass(params),
      },
      suppressRowClickSelection: false,
      columnDefs: this.fileDetailsColDefs(),
      statusBar: {
        statusPanels: [
          {
            statusPanel: CustomAggregateBarComponent,
          },
        ],
      },
      getRowClass: params =>
        this.highlightService.getRowClass(params.node.id ?? ''),
      getContextMenuItems: params =>
        contextMenuItems(
          this.highlightService.createHighlightMenuItems(params),
          params,
        ),
      onModelUpdated: (event: ModelUpdatedEvent) => {
        if (event.newData && !event.api.isColumnFilterPresent()) {
          this._getAndApplyFilter()
        }
      },
      onFirstDataRendered: async event => {
        if (this.externalRawData == null) {
          return
        }

        const fileGridKey = generateUniqueFileKey(
          this.externalRawData,
          ExternalFileViewerDetailsGridKey,
        )
        await this.highlightService.setGridKeyForPersistentStorage(
          fileGridKey,
          this.memoryStorageService,
          event.api,
        )
      },
      isExternalFilterPresent: () => true,
      doesExternalFilterPass: (node: RowNode) => this.testContainsInNode(node),
      suppressCsvExport: false,
    }
  }

  testContainsInNode(node: RowNode): boolean {
    const splittedText: string[] = this.searchText.value
      ? this.searchText.value.split(',')
      : []
    if (splittedText.length == 0) return true
    return splittedText.some(splittedString =>
      JSON.stringify(Object.values(node.data))
        .toLowerCase()
        .includes(splittedString.toLowerCase()),
    )
  }

  setExternalFilter(filterText: string) {
    this.searchText.setValue(filterText)
    this.agGridApi?.api.onFilterChanged()
  }

  /**
   * @internal
   * @param event
   */
  async onGridReady(event: GridReadyEvent) {
    if (this.externalRawData == null) {
      return
    }

    this.agGridApi = event
    await this.fetchFileData(this.externalRawData)

    const gridKey = generateUniqueFileKey(
      this.externalRawData,
      ExternalFileViewerDetailsGridKey,
    )

    this.preferences
      .getDensityLevel()
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(level =>
        enableHeliosGridDensityLevel(event.api, DensityLevel.Compact === level),
      )

    await this.savedViewService.manageSavedView(
      event,
      gridKey,
      this.destroySubject$,
      false,
    )

    enableHeliosMultiRowDragSelection(event.api)
    enableHeliosCopyBehavior(event.api)
  }

  private _getAndApplyFilter() {
    if (this.externalRawData == null) {
      return
    }

    const gridKey = generateUniqueFileKey(
      this.externalRawData,
      ExternalFileViewerDetailsGridKey,
    )
    // get state from store
    const filterState = this.savedViews?.find(view => view.gridKey === gridKey)
    if (filterState) {
      // Apply preserved filters once grid is rendered
      this.agGridApi?.api.setFilterModel(filterState.savedView.filterState)
    }
  }

  fileDetailsColDefs(columnNames: string[] = []): ColDef[] {
    return columnNames.map(
      column =>
        ({
          field: column,
        } as ColDef),
    )
  }

  private registerHotkeys() {
    const ordinals = ['1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th']
    for (let i = 0; i < ordinals.length; i++) {
      this.hotkeyDestroyStack.push(
        this.hotkeysService.bind(
          `highlight-row-with-${ordinals[i]}-color-in-external-file-viewer` as HighlightRowsInExternalFileViewer,
          () =>
            this.highlightService.setHighlightOnExternalAction(
              this.agGridApi,
              i,
            ),
        ),
      )
    }
  }
  private unregisterHotkeys() {
    this.hotkeyDestroyStack.forEach(hotkeyDestroyFn => hotkeyDestroyFn())
    this.hotkeyDestroyStack.length = 0
  }

  getCellClass(params: CellClassParams) {
    return this.valueContainsSearchText(params.value)
      ? 'mdi mdi-check-circle text-success'
      : ''
  }

  getCellStyle(params: CellClassParams): CellStyle {
    const cellStyle = this.highlightService.getCellStyle(
      params.node.id ?? '',
      params.colDef.field ?? '',
    ) as CellStyle

    return this.valueContainsSearchText(params.value)
      ? {
          ...cellStyle,
          'font-weight': 'bold',
        }
      : cellStyle
  }

  private valueContainsSearchText(value: string | undefined) {
    const searchText = (this.searchText.value ?? '').trim().toLowerCase()
    if (searchText === '') {
      return false
    }

    const cellValue = (value ?? '').trim().toLowerCase()
    if (cellValue === '') {
      return false
    }

    return cellValue.includes(searchText)
  }
}
